import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  Chip,
  Rating,
  Stack,
  Container,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Phone,
  Instagram,
  Facebook,
  YouTube,
  Flag,
  ArrowBackIos,
  ArrowForwardIos,
  LanguageTwoTone,
  MapOutlined,
  Check,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { request } from '../../Utils/request';
import { togglePlaceFavorite, getPublicUrlFromFileKey } from '../../services/API/Api';
import { updatePlace, updatePlaceReview } from '../../store/slices/PlacesSlice';
import { StaticMap } from '../../Components/Map';
import AddReviewDialog from '../../Components/AddReviewDialog';
import ReviewCard from '../../Components/ReviewCard';
import StarIcon from '@mui/icons-material/Star';
import ImageWithLoader from '../../Components/ImageWithLoader';
import CircleIcon from '@mui/icons-material/Circle';
import PlaceTabs from '../../Components/PlaceTabs';
import PlaceRelatedPost from './PlaceRelatedPost';
import ReportModal from '../../Components/ReportModal';
import { calculateDistance } from '../../Utils/googleMapsDistance';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { ShortsCard } from '../Shorts/ShortsCards';
import ShareModal from '../../Components/ShareModal';
import { handleFollow, handleLike, handleBookmark } from '../Post/PostCommonApi';
import { PostUI } from '../Post/PostUI';

interface Place {
  _id: string;
  name: string;
  description: string;
  categories: string[];
  tags: string[];
  type: string;
  featured: boolean;
  location: {
    type: string;
    zoom: number;
    lat: number;
    lng: number;
  };
  address: string;
  owner_details: {
    name: string;
    email: string;
    phone: string;
  };
  files: Array<{
    fileURL: string;
    fileType: string;
  }>;
  rating?: number;
  // reviews: {
  //   reviews: Review[];
  // };
  distance?: string;
  createdAt: string;
  updatedAt: string;
  social: { key: string; value: string }[];
  isLiked: boolean;
  topReviews?: Review[];
  isVisited: boolean;
  reports?: Array<{
    profileId: string;
    reason: string;
    description: string;
    status: string;
    reportedAt: string;
    _id: string;
  }>;
}

interface Review {
  user: {
    _id: string;
    username: string;
    avatar?: string; // optional future support
    profilePhoto: {
      fileKey: string;
      fileType: string;
      featured: boolean;
      url: string;
    };
  };
  rating: number;
  review: string;
  ratedAt: string;
}

interface ReviewResponse {
  totalReviews: number;
  averageRating: number;
  reviews: Review[];
  currentUserRating: number;
  currentUserReview: string;
}

type ReviewInput = Pick<Review, 'rating' | 'review'>;

const PlaceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const [place, setPlace] = useState<Place | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const userLocation = useSelector((state: RootState) => state.location.location);
  const user = useSelector((state: RootState) => state.user);

  const [openReview, setOpenReview] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const placeData = useSelector((state: RootState) => state.places.places);
  const [tabValue, setTabValue] = useState<number>(0);

  // Shorts-related state
  const [shortsData, setShortsData] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [selectedShortForShare, setSelectedShortForShare] = useState<any>(null);
  const [openPlaceShareModal, setOpenPlaceShareModal] = useState(false);
  const [showShortsFullScreen, setShowShortsFullScreen] = useState(false);
  const [selectedShortIndex, setSelectedShortIndex] = useState<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<HTMLDivElement[]>([]);

  // Posts-related state
  const [postsData, setPostsData] = useState<any[]>([]);
  const [showPostsFullScreen, setShowPostsFullScreen] = useState(false);
  const [selectedPostIndex, setSelectedPostIndex] = useState<number>(0);
  const postsScrollContainerRef = useRef<HTMLDivElement>(null);



  useEffect(() => {
    if (id) {
      fetchPlaceDetails(id);
    }
  }, [id, dispatch]);

  // Calculate distance when user location and place data are available
  useEffect(() => {
    const calculatePlaceDistance = async () => {
      if (userLocation?.lat && userLocation?.lng && place?.location?.lat && place?.location?.lng) {
        try {
          const calculatedDistance = await calculateDistance(
            userLocation.lat,
            userLocation.lng,
            place.location.lat,
            place.location.lng
          );
          setDistance(calculatedDistance);
        } catch (error) {
          console.error('Error calculating distance:', error);
          setDistance(null);
        }
      }
    };

    calculatePlaceDistance();
  }, [userLocation, place]);

  const fetchPlaceDetails = useCallback(async (placeId: string) => {
    try {
      const response = await request<{ success: boolean; data: Place, auth: boolean }>(`/places/${placeId}`, { authorization: true });
      if (response.success) {
        console.log('response.data place by id', response.data);
        setPlace(response.data);
        // Update the global store so other components have the full place data
        dispatch(updatePlace(response.data));
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      // setLoading(false);
    }
  }, [dispatch]);

  const createReview = async (placeId: string, reviewData: ReviewInput) => {
    if (!place) return;
    
    try {
      const res = await request<{ success: boolean; data: ReviewResponse }>(`/places/${placeId}/review`, {
        authorization: true, 
        method: 'POST', 
        body: reviewData
      });
      console.log('create review res', res);
      if (res.success === true) {
        // Update the store with the new review data
        dispatch(updatePlaceReview({
          _id: placeId,
          reviewData: res.data
        }));
        
        // Also update local state to reflect changes immediately
        const updatedPlace = {
          ...place,
          totalReviews: res.data.totalReviews,
          averageRating: res.data.averageRating,
          reviews: res.data.reviews,
          currentUserRating: res.data.currentUserRating,
          currentUserReview: res.data.currentUserReview,
          topReviews: res.data.reviews
        };
        setPlace(updatedPlace);
      }
    } catch (error) {
      console.error('Error creating review:', error);
    }
  };

  const handleFavoriteToggle = async (placeId: string) => {
    if (!place) return;
    
    try {
      const res = await togglePlaceFavorite(placeId);
      
      if (res.success) {
        // Toggle the isLiked state
        const newIsLiked = !place.isLiked;
        // Create the updated place object
        const updatedPlace = { ...place, isLiked: newIsLiked };
        // Update local state first
        setPlace(updatedPlace);
        // Then update the store
        dispatch(updatePlace(updatedPlace));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Check if current user has already reported this place
  const currentUserId = user?.user?._id || user?.user?.id;
  const isAlreadyReported = place?.reports?.some(
    (report) => report.profileId === currentUserId
  ) || false;

  const handleReportPlace = async (reportData: { reason: string; description: string }) => {
    if (!place || !currentUserId) return;
    
    try {
      const res = await request<{ success: boolean; data?: any }>(`/places/${place._id}/report`, {
        authorization: true,
        method: 'POST',
        body: reportData,
      });
      console.log('post report  res', res);
      
      if (res.success) {
        // Create new report object
        const newReport = {
          profileId: currentUserId,
          reason: reportData.reason,
          description: reportData.description,
          status: 'pending',
          reportedAt: new Date().toISOString(),
          _id: res.data?._id || `temp-${Date.now()}`, // Use API response ID if available, otherwise temp ID
        };

        // Update reports array
        const updatedReports = [...(place.reports || []), newReport];
        
        // Create updated place object
        const updatedPlace = { 
          ...place, 
          reports: updatedReports 
        };
        
        // Update local state first
        setPlace(updatedPlace);
        
        // Then update Redux store
        dispatch(updatePlace(updatedPlace));
        
        alert('Thank you for your report. We will review it shortly.');
      }
    } catch (err) {
      console.error('err', err);
      alert('Failed to submit report. Please try again.');
    }
  };

  const handlePlaceShare = () => {
    setOpenPlaceShareModal(true);
  };

  const getPlaceShareUrl = () => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin;
    return `${baseUrl}/places/${place?._id}`;
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch shorts for this place
  const fetchPlaceShorts = useCallback(async (placeId: string) => {
    try {
      const res = await request<{ success: boolean, data: { posts: any[] } }>(
        `/places/${placeId}/posts`,
        {
          authorization: true,
          method: 'GET',
        },
      );

      // Resolve view URLs for videos that only have fileKey
      const processedPosts = await Promise.all(
        res.data.posts.map(async (post: any) => {
          if (!post?.files?.length) return post;
          const firstFile = post.files[0];

          // If fileURL is missing but we have a fileKey (likely a video), fetch the public URL
          if (!firstFile.fileURL && firstFile.fileKey) {
            try {
              const viewRes = await getPublicUrlFromFileKey(firstFile.fileKey, 'post');
              firstFile.fileURL = viewRes?.data?.files?.[0]?.viewUrl || '';
            } catch (error) {
              console.error('Failed to fetch viewUrl for video', error);
            }
          }
          return post;
        }),
      );

      // Filter only videos (shorts)
      const shorts = processedPosts.filter((post: any) => {
        const fileType = post?.files?.[0]?.fileType || '';
        return fileType.startsWith('video');
      });

      setShortsData(shorts);
    } catch (error) {
      console.error('Error fetching place shorts:', error);
    }
  }, []);

  // Fetch posts for this place
  const fetchPlacePosts = useCallback(async (placeId: string) => {
    try {
      const res = await request<{ success: boolean, data: { posts: any[] } }>(
        `/places/${placeId}/posts`,
        {
          authorization: true,
          method: 'GET',
        },
      );

      // Resolve view URLs for images that only have fileKey
      const processedPosts = await Promise.all(
        res.data.posts.map(async (post: any) => {
          if (!post?.files?.length) return post;
          const firstFile = post.files[0];

          // If fileURL is missing but we have a fileKey, fetch the public URL
          if (!firstFile.fileURL && firstFile.fileKey) {
            try {
              const viewRes = await getPublicUrlFromFileKey(firstFile.fileKey, 'post');
              firstFile.fileURL = viewRes?.data?.files?.[0]?.viewUrl || '';
            } catch (error) {
              console.error('Failed to fetch viewUrl for file', error);
            }
          }
          return post;
        }),
      );

      // Filter only non-video posts (images)
      const posts = processedPosts.filter((post: any) => {
        const fileType = post?.files?.[0]?.fileType || '';
        return !fileType.startsWith('video');
      });

      setPostsData(posts);
    } catch (error) {
      console.error('Error fetching place posts:', error);
    }
  }, []);

  // Fetch shorts when shorts tab is active
  useEffect(() => {
    if (tabValue === 2 && place?._id && !showShortsFullScreen) {
      fetchPlaceShorts(place._id);
    }
  }, [tabValue, place?._id, fetchPlaceShorts, showShortsFullScreen]);

  // Fetch posts when posts tab is active
  useEffect(() => {
    if (tabValue === 1 && place?._id && !showPostsFullScreen) {
      fetchPlacePosts(place._id);
    }
  }, [tabValue, place?._id, fetchPlacePosts, showPostsFullScreen]);

  // Scroll to selected post when entering full-screen mode
  useEffect(() => {
    if (showPostsFullScreen && postsData.length > 0) {
      setTimeout(() => {
        const postElement = document.querySelector(`[data-post-id="${postsData[selectedPostIndex]?._id}"]`);
        if (postElement) {
          postElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  }, [showPostsFullScreen, selectedPostIndex, postsData]);

  // Scroll to selected short when entering full-screen mode
  useEffect(() => {
    if (showShortsFullScreen && shortsData.length > 0) {
      setTimeout(() => {
        const shortElement = document.querySelector(`[data-short-id="${shortsData[selectedShortIndex]?._id}"]`);
        if (shortElement) {
          shortElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  }, [showShortsFullScreen, selectedShortIndex, shortsData]);

  // Prevent body scroll when in full screen mode
  useEffect(() => {
    if (showShortsFullScreen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showShortsFullScreen]);

  // Handle scroll for shorts visibility
  useEffect(() => {
    if (tabValue !== 2 || shortsData.length === 0) return;

    const handleScroll = () => {
      // Calculate visibility ratio for each video element
      const visibilityRatios = videoRefs.current.map((ref) => {
        if (!ref) return 0;
        const rect = ref.getBoundingClientRect();
        const elementHeight = rect.height || 1;
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        const ratio = visibleHeight / elementHeight;
        return ratio < 0 ? 0 : ratio;
      });

      // Find the element that is at least 60% visible and has the highest ratio
      let candidateIndex = activeIndex;
      let highestRatio = 0;

      visibilityRatios.forEach((ratio, idx) => {
        if (ratio >= 0.6 && ratio > highestRatio) {
          highestRatio = ratio;
          candidateIndex = idx;
        }
      });

      if (candidateIndex !== activeIndex) {
        setActiveIndex(candidateIndex);
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeIndex, shortsData.length, tabValue]);

  // Handle browser back button when in full-screen shorts
  useEffect(() => {
    if (!showShortsFullScreen) {
      return;
    }

    const handlePopState = () => {
      // Close full-screen when back button is pressed
      setShowShortsFullScreen(false);
      setTabValue(2); // Ensure shorts tab is active
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showShortsFullScreen]);

  //   if (loading) {
  //     return (
  //       <Container maxWidth="sm" sx={{ py: 2 }}>
  //         <Typography>Loading place details...</Typography>
  //       </Container>
  //     );
  //   }

  if (!place) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography>Place not found</Typography>
      </Container>
    );
  }

  const handleClick = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${place?.location?.lat},${place?.location?.lng}`;
    window.open(url, "_blank"); // Open in new tab or Google Maps app on mobile
  };

  const mainImage = place.files && place.files.length > 0
    ? place.files[0].fileURL
    : 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80';

  const averageRating = place.rating || 4;

  // Dynamically choose a color for the rating badge
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return '#2E7D32'; // green for high ratings
    if (rating >= 3) return '#F9A825'; // amber for medium ratings
    if (rating > 0) return '#EF5350'; // red for low ratings
    return '#BDBDBD'; // grey when there is no rating
  };

  const handleExplore = () => {
    navigate(`/places/${id}/posts`, { state: { placeId: place._id } })
  }

  const handlePrevImage = () => {
    if (place?.files && place.files.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? place.files.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (place?.files && place.files.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === place.files.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleMarkAsVisited = async (placeId: string) => {
    if (!place) return;
    
    try {
      const res = await request<{ status: boolean, data: { isVisited: boolean } }>(`/profile/visited-places/${placeId}`, {
        authorization: true,
        method: 'POST',
      });
      
      if (res.status === true) {
        const result = res.data.isVisited;
        // Create the updated place object
        const updatedPlace = { ...place, isVisited: result };
        // Update local state first
        setPlace(updatedPlace);
        // Then update the store
        // dispatch(updatePlaceReview(updatedPlace));
        
      }
    } catch (err) {
      console.error('Error marking place as visited:', err);
    }
  };

  // Shorts handlers
  const handleShortShare = (short: any) => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    const shareUrl = `${baseUrl}/shorts/${short._id}`;
    const shareText = `Check out this short: ${short.description || short.title || 'Amazing video'}`;
    
    setSelectedShortForShare({
      ...short,
      shareUrl,
      shareText
    });
    setOpenShareModal(true);
  };

  const handleShortLikeClick = (id: string, isLiked: boolean) => {
    handleLike(id, dispatch, 'video');
  };

  const handleShortBookmarkClick = (id: string, isSaved: boolean) => {
    handleBookmark(id, dispatch, 'video');
  };

  const handleToggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  // Handle post click - show in full screen
  const handlePostClick = (postId: string) => {
    const index = postsData.findIndex(post => post._id === postId);
    if (index !== -1) {
      setSelectedPostIndex(index);
      setShowPostsFullScreen(true);
    }
  };

  // Handle back from full screen posts view
  const handleBackFromPosts = () => {
    setShowPostsFullScreen(false);
  };

  // Handle short click - show in full screen
  const handleShortClick = (shortId: string) => {
    const index = shortsData.findIndex(short => short._id === shortId);
    if (index !== -1) {
      setSelectedShortIndex(index);
      setShowShortsFullScreen(true);
      // Ensure shorts tab is active
      setTabValue(2);
      // Push state to history so back button can close full-screen
      window.history.pushState({ fullScreenShorts: true }, '');
    }
  };

  // Handle back from full screen shorts view
  const handleBackFromShorts = () => {
    setShowShortsFullScreen(false);
    setTabValue(2); // Ensure shorts tab is active
  };

  // Render full screen shorts view using portal to escape layout hierarchy
  const fullScreenShortsView = showShortsFullScreen ? (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300, bgcolor: 'black', width: '100vw', height: '100vh' }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          height: 'calc(100vh - 56px)',  // Match Shorts.tsx
          overflowY: 'scroll',
          scrollSnapType: 'y mandatory',
          pb: { xs: '56px', sm: 0 },  // Add bottom padding
        }}
      >
        {shortsData.map((item, index) => (
          <Box key={item._id} data-short-id={item._id}>
            <ShortsCard
              item={item}
              index={index}
              videoRefs={videoRefs}
              activeIndex={activeIndex}
              handleFollow={() => handleFollow(item?.author?._id, item?._id, dispatch, 'video')}
              handleShare={() => handleShortShare(item)}
              isPreview={false}
              currentUser={user?.user}
              onLike={handleShortLikeClick}
              onBookmark={handleShortBookmarkClick}
              isMuted={isMuted}
              onToggleMute={handleToggleMute}
            />
          </Box>
        ))}

        <ShareModal
          open={openShareModal}
          onClose={() => {
            setOpenShareModal(false);
            setSelectedShortForShare(null);
          }}
          title={selectedShortForShare?.description || selectedShortForShare?.title || 'Short Video'}
          shareUrl={selectedShortForShare?.shareUrl || ''}
          shareText={selectedShortForShare?.shareText || ''}
        />
      </Box>
    </Box>
  ) : null;

  return (
    <>
      {showShortsFullScreen && createPortal(fullScreenShortsView, document.body)}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', ...(showShortsFullScreen && { display: 'none' }) }}>
        <TopNavBar title={place.name} canGoBack onBackClick={() => navigate('/')} />
        <PlaceTabs value={tabValue} onChange={(e) => {
          setTabValue(e)
        }} />

        <>
      {tabValue === 0 && (
        <Container maxWidth="sm" sx={{ flex: 1, overflowY: 'auto' }}>
        <>

          {/* Image Carousel */}
          <Box sx={{ p: 3, mt: 0, borderRadius: '16px', borderColor: 'grey', borderWidth: '1px ', borderStyle: '-moz-initial', my: 2, bgcolor: 'white', position: 'relative' }}>
            {/* Main Image Display */}

            <Box sx={{ position: 'relative', width: '100%', height: '300px', overflow: 'hidden', borderRadius: '12px' }}>
              <Box
                sx={{
                  display: 'flex',
                  width: `${(place?.files?.length || 1) * 100}%`,
                  height: '100%',
                  transform: `translateX(-${currentImageIndex * (100 / (place?.files?.length || 1))}%)`,
                  transition: 'transform 0.3s ease-in-out',
                }}
              >
                {place?.files && place.files.length > 0 ? (
                  place.files.map((file, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: `${100 / place.files.length}%`,
                        height: '100%',
                        flexShrink: 0,
                      }}
                    >
                      <ImageWithLoader
                        src={file.fileURL}
                        alt={`${place.name} - Image ${index + 1}`}
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        containerSx={{ width: '100%', height: '100%' }}
                      />
                    </Box>
                  ))
                ) : (
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <ImageWithLoader
                      src={mainImage}
                      alt={place.name}
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      containerSx={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                )}
              </Box>


              {/* Navigation Arrows */}
              {place?.files && place.files.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <ArrowBackIos />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Image Indicators */}
            {place?.files && place.files.length > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
                {place.files.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: currentImageIndex === index ? '#ff6b35' : 'rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: currentImageIndex === index ? '#ff6b35' : 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                  />
                ))}
              </Box>
            )}



            <Box sx={{
              position: 'absolute',
              top: 26,
              right: 26,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <IconButton
                onClick={() => {
                  const placeId = place._id;
                  handleFavoriteToggle(placeId)
                }}
                sx={{
                  bgcolor: 'white',
                  '&:hover': { bgcolor: 'white' },
                  boxShadow: 1
                }}
              >
                {place.isLiked ? <Favorite sx={{ color: 'red' }} /> : <FavoriteBorder />}
              </IconButton>
              <Chip
                label={`${currentImageIndex + 1} / ${place?.files?.length || 1}`}
                size="small"
                sx={{
                  bgcolor: 'white',
                  fontWeight: 'bold',
                  boxShadow: 1
                }}
              />
            </Box>

            {/* Share Icon - Bottom Right */}
            <Box sx={{
              position: 'absolute',
              bottom: 32,
              right: 32,
              
            }}>
              <IconButton
                onClick={handlePlaceShare}
                size="small"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.6)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                  boxShadow: 1,
                  width: 32,
                  height: 32
                }}
              >
                <ShareIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Place Details */}
          <Paper sx={{ p: 3, mt: 0, borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Typography fontWeight='medium' gutterBottom>
                  {place.name}
                </Typography>
                <CircleIcon sx={{ fontSize: 6, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {distance ? `${distance}km` : place.distance || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                icon={place.isVisited ? <Check sx={{ color: '#008387',fontSize:16 }} />: undefined}
                onClick={() => {
                  handleMarkAsVisited(place._id)
                }} label={place.isVisited ? "Visited" : "Mark as visited"} variant='outlined' sx={{ borderRadius: 1, borderWidth: 1, borderColor:place.isVisited ? '#008387' : '#CAC4D0',color:place.isVisited ? '#008387' : 'black' ,py:2.5}} />
                
                {/* Rating */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: getRatingColor(averageRating), color: 'white', paddingX: 2, paddingY: 1, borderRadius: '8px' }}>
                  <Typography color='white'>{averageRating}</Typography>
                  <StarIcon sx={{ color: 'white' }} />
                </Box>
              </Box>
            </Box>


            {/* Category Tags */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {place.categories?.map((category, index) => (
                <Chip
                  key={index}
                  variant='outlined'
                  label={category}
                  size="small"
                  sx={{ borderColor: '#f0f0f0', color: '#666', borderRadius: '8px', paddingX: 1, paddingY: 0.5, fontVariant: 'medium' }}
                />
              ))}
            </Stack>



            {/* Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {place.description || "Perched on dramatic cliffs overlooking the Aegean Sea, Santorini is a postcard-perfect island known for its whitewashed buildings with blue domes."}
            </Typography>
          </Paper>

          {/* Map Section */}
          <Paper sx={{ p: 2, mt: 1, borderRadius: '16px', bgcolor: 'white' }}>
            {place.location && (
              <Box onClick={() => {
                handleClick()
              }}>

                <StaticMap
                  location={{
                    coordinates: {
                      lat: place?.location?.lat,
                      lng: place?.location?.lng
                    },
                    // zoom: place?.location?.zoom || 25
                  }}
                  height={200}
                  width="100%"
                  borderRadius="16px"
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
              <MapOutlined
                sx={{ fontSize: 20, color: 'text.secondary', mx: 2 }}
              />
              <Box onClick={() => {
                handleClick()
              }}>
                <Typography variant="body2" fontWeight="500">
                  {place.address}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2, borderColor: 'grey', borderWidth: '.5px ', borderStyle: '-moz-initial' }} />
            {place.owner_details?.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {place.owner_details.phone}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 2, borderColor: 'grey', borderWidth: '.5px ', borderStyle: '-moz-initial' }} />
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <IconButton onClick={() => {
                window.open(place.social.find(item => item.key === 'instagram')?.value, '_blank');
              }} sx={{ bgcolor: '#E1306C', color: 'white', '&:hover': { bgcolor: '#C13584' } }}>
                <LanguageTwoTone />
              </IconButton>
              <IconButton onClick={() => {
                window.open(place.social.find(item => item.key === 'facebook')?.value, '_blank');
              }} sx={{ bgcolor: '#1877F2', color: 'white', '&:hover': { bgcolor: '#166FE5' } }}>
                <Facebook />
              </IconButton>
              <IconButton sx={{ bgcolor: '#E1306C', color: 'white', '&:hover': { bgcolor: '#C13584' } }}>
                <Instagram />
              </IconButton>
              <IconButton sx={{ bgcolor: '#FF0000', color: 'white', '&:hover': { bgcolor: '#CC0000' } }}>
                <YouTube />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'grey', borderWidth: '.5px ', borderStyle: '-moz-initial' }} />
            {/* Report Listing */}
            <Box 
              onClick={() => !isAlreadyReported && setReportModalOpen(true)} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: isAlreadyReported ? 'not-allowed' : 'pointer',
                opacity: isAlreadyReported ? 0.6 : 1,
                '&:hover': { bgcolor: isAlreadyReported ? 'transparent' : '#f5f5f5' }, 
                p: 1, 
                borderRadius: 1 
              }}

            >
              <Flag sx={{ mr: 1, color: isAlreadyReported ? '#9E9E9E' : '#BA1A1A', fontSize: 20 }} />
              <Typography variant="body2" color={isAlreadyReported ? '#9E9E9E' : '#BA1A1A'} fontWeight="500">
                {isAlreadyReported ? 'Already reported' : 'Report listing'}
              </Typography>
            </Box>
          </Paper>




          {/* Reviews Section */}
          <Typography onClick={() => {
            navigate(`/places/${id}/reviews`)
          }} variant="h6" fontWeight="600" sx={{ my: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            Reviews <ArrowForwardIos sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Typography>
        

          <Button
            onClick={() => setOpenReview(true)}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: '100px',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#F57C00',
              color: '#F57C00',
              '&:hover': { bgcolor: '#F57C00' },
            }}
          >
            Post review
          </Button>

          {/* Review List */}
       
          {place?.topReviews?.map((rev) => (
            <ReviewCard
              key={`${rev.user._id}-${rev.ratedAt}`}
              userName={rev.user.username}
              avatar={rev.user?.profilePhoto?.url || ''}
              date={rev.ratedAt}
              rating={rev.rating}
              review={rev.review}
            />
          ))}

          {/* Add Review Dialog */}
          <AddReviewDialog
            open={openReview}
            onClose={() => setOpenReview(false)}
            onSubmit={(data) => {
              createReview(place._id, data);
              setOpenReview(false);
            }}
          />
          
          {/* Report Modal */}
          <ReportModal
            open={reportModalOpen}
            onClose={() => setReportModalOpen(false)}
            onSubmit={handleReportPlace}
            title="Report this listing"
          />

          {/* Place Share Modal */}
          <ShareModal
            open={openPlaceShareModal}
            onClose={() => setOpenPlaceShareModal(false)}
            title={place.name}
            shareUrl={getPlaceShareUrl()}
            shareText={place.description || `Check out ${place.name} on iFinds`}
          />

          {/* Bottom spacing */}
          <Box sx={{ height: 100 }} />
        </>
        </Container>
      )}
      {tabValue === 1 && (
        <>
          {showPostsFullScreen ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
              <TopNavBar title="Posts" canGoBack onBackClick={handleBackFromPosts} />
              <Box
                ref={postsScrollContainerRef}
                sx={{
                  flex: 1,
                  overflowY: 'scroll',
                  position: 'relative',
                }}
              >
                {postsData.map((item, index) => (
                  <Box 
                    key={item._id} 
                    data-post-id={item._id}
                    ref={(el: HTMLDivElement | null) => {
                      if (el && index === selectedPostIndex) {
                        setTimeout(() => {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }
                    }}
                  >
                    <PostUI item={item} isPreview={false} />
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Container maxWidth="sm" sx={{ flex: 1, overflowY: 'auto' }}>
              <Typography>Posts</Typography>
              <PlaceRelatedPost placeId={place?._id} type="posts" onPostClick={handlePostClick} />
            </Container>
          )}
        </>
      )}
      {tabValue === 2 && (
        <Container maxWidth="sm" sx={{ flex: 1, overflowY: 'auto' }}>
          <Typography>Shorts</Typography>
          <PlaceRelatedPost placeId={place?._id} type="shorts" onPostClick={handleShortClick} />
        </Container>
      )}
      </>
      </Box>
    </>
  );
};

export default PlaceDetail; 
